import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { Patient, AIReview } from '../common/models/types';
import ReportPrintView from '../components/organisms/ReportPrintView';
import { reorderSectionsByPromptType } from './reviewContentUtils';

/**
 * 보고서를 새창에서 PDF로 프린트할 수 있는 형태로 표시
 * legacy의 openInNewWindow() 함수를 참고하여 구현
 */
export const openReportInNewWindow = (
  patient: Patient,
  review: AIReview,
  consultationNotes?: string
) => {
  try {
    // promptType에 따라 reviewResult 가공
    const processedReviewResult = reorderSectionsByPromptType(
      review.reviewResult || '', 
      review.promptType
    );
    
    // 가공된 reviewResult로 새로운 review 객체 생성
    const processedReview = {
      ...review,
      reviewResult: processedReviewResult
    };
    
    // React 컴포넌트를 HTML 문자열로 변환
    const componentHTML = ReactDOMServer.renderToString(
      React.createElement(ReportPrintView, {
        patient,
        review: processedReview,
        consultationNotes,
      })
    );

    // 새창을 열고 HTML 내용을 작성
    const newWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
    
    if (!newWindow) {
      alert('팝업이 차단되었습니다. 팝업 차단을 해제하고 다시 시도해주세요.');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>검토 결과 - 환자${patient.id}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            * {
              box-sizing: border-box;
            }
            
            body {
              margin: 0;
              padding: 0;
              font-family: "Malgun Gothic", Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              background-color: #f5f5f5;
            }
            
            /* 프린트 스타일 */
            @media print {
              * {
                print-color-adjust: exact !important;
                -webkit-print-color-adjust: exact !important;
              }
              
              @page {
                size: A4;
                margin: 2cm;
              }
              
              body {
                background-color: white !important;
              }
              
              .no-print {
                display: none !important;
              }
            }
            
            /* 인쇄 버튼 스타일 */
            .print-controls {
              position: fixed;
              top: 20px;
              right: 20px;
              z-index: 1000;
              display: flex;
              gap: 10px;
            }
            
            .btn {
              padding: 10px 20px;
              border: none;
              border-radius: 5px;
              cursor: pointer;
              font-size: 14px;
              font-weight: bold;
              transition: background-color 0.3s;
            }
            
            .btn-primary {
              background-color: #007bff;
              color: white;
            }
            
            .btn-primary:hover {
              background-color: #0056b3;
            }
            
            .btn-secondary {
              background-color: #6c757d;
              color: white;
            }
            
            .btn-secondary:hover {
              background-color: #545b62;
            }
            
            /* 추가 입력 박스 스타일 */
            .additional-input-section {
              margin-top: 30px;
              padding: 20px;
              border: 2px solid #007bff;
              border-radius: 8px;
              background-color: #f8f9ff;
            }
            
            .input-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
            }
            
            .input-title {
              font-size: 18px;
              font-weight: bold;
              color: #007bff;
            }
            
            .input-textarea {
              width: 100%;
              min-height: 150px;
              padding: 12px;
              border: 1px solid #ccc;
              border-radius: 5px;
              font-family: "Malgun Gothic", Arial, sans-serif;
              font-size: 14px;
              line-height: 1.5;
              resize: vertical;
            }
            
            .input-textarea:focus {
              outline: none;
              border-color: #007bff;
              box-shadow: 0 0 5px rgba(0, 123, 255, 0.3);
            }
            
            .convert-btn {
              margin-top: 10px;
              padding: 8px 16px;
              background-color: #28a745;
              color: white;
              border: none;
              border-radius: 5px;
              cursor: pointer;
              font-weight: bold;
            }
            
            .convert-btn:hover {
              background-color: #218838;
            }
            
            .converted-content {
              margin-top: 20px;
              padding: 15px;
              border: 1px solid #ddd;
              border-radius: 5px;
              background-color: white;
              min-height: 50px;
            }
            
            .help-text {
              font-size: 12px;
              color: #666;
              margin-bottom: 10px;
            }
          </style>
        </head>
        <body>
          <!-- 인쇄 버튼 (인쇄 시에는 숨김) -->
          <div class="print-controls no-print">
            <button class="btn btn-primary" onclick="window.print()">📄 PDF로 저장</button>
            <button class="btn btn-secondary" onclick="window.close()">❌ 닫기</button>
          </div>
          
          <!-- 보고서 내용 -->
          <div id="report-content">
            ${componentHTML}
          </div>
          
          <!-- 추가 입력 섹션 (인쇄 시에는 숨김) -->
          <div class="additional-input-section no-print">
            <div class="input-header">
              <div class="input-title">📝 추가 검토 의견 작성</div>
            </div>
            
            <div class="help-text">
              마크다운 문법을 사용할 수 있습니다: # 제목, ## 부제목, **굵게**, *기울임*, - 목록
            </div>
            
            <textarea 
              id="additional-review" 
              class="input-textarea" 
              placeholder="추가 검토 의견을 입력하세요..."
            ></textarea>
            
            <button class="convert-btn" onclick="convertAndAppendReview()">
              ✅ 변환하여 보고서에 추가
            </button>
            
            <div id="converted-preview" class="converted-content" style="display: none;">
              <strong>변환 미리보기:</strong>
              <div id="preview-content"></div>
            </div>
          </div>
          
          <script>
            console.log('Script loaded');
            
            // 마크다운 변환 함수
            function convertMarkdownToHTML(text) {
              console.log('Converting markdown:', text);
              if (!text) return "";
              
              var html = text;
              
              // 헤더 변환 (순서 중요: 긴 것부터)
              html = html.replace(/^### (.*)$/gm, '<h5 style="background-color: #b48ead; color: black; padding: 5px; margin: 20px 0 10px 0;">$1</h5>');
              html = html.replace(/^## (.*)$/gm, '<h4 style="background-color: #a3be8c; color: black; padding: 5px; margin: 20px 0 10px 0;">$1</h4>');
              html = html.replace(/^# (.*)$/gm, '<h3 style="background-color: #ebcb8b; color: black; padding: 5px; margin: 20px 0 10px 0;">$1</h3>');
              
              // 강조 텍스트 (굵게 먼저, 기울임 나중에)
              html = html.replace(/\\*\\*(.*?)\\*\\*/g, '<strong style="color: #d63384; font-weight: bold;">$1</strong>');
              html = html.replace(/\\*([^*\\n]+)\\*/g, '<em style="color: #0d6efd;">$1</em>');
              
              // 목록 처리
              var lines = html.split('\\n');
              var result = [];
              var inUnorderedList = false;
              var inOrderedList = false;
              
              for (var i = 0; i < lines.length; i++) {
                var line = lines[i];
                var trimmedLine = line.trim();
                
                if (trimmedLine.match(/^\\d+\\./)) {
                  // 번호 목록
                  if (!inOrderedList) {
                    if (inUnorderedList) {
                      result.push('</ul>');
                      inUnorderedList = false;
                    }
                    result.push('<ol style="margin: 10px 0; padding-left: 20px;">');
                    inOrderedList = true;
                  }
                  var content = trimmedLine.replace(/^\\d+\\.\\s*/, '');
                  result.push('<li style="margin: 5px 0;">' + content + '</li>');
                } else if (trimmedLine.match(/^-/)) {
                  // 일반 목록
                  if (!inUnorderedList) {
                    if (inOrderedList) {
                      result.push('</ol>');
                      inOrderedList = false;
                    }
                    result.push('<ul style="margin: 10px 0; padding-left: 20px;">');
                    inUnorderedList = true;
                  }
                  var content = trimmedLine.replace(/^-\\s*/, '');
                  result.push('<li style="margin: 5px 0;">' + content + '</li>');
                } else {
                  // 일반 텍스트
                  if (inUnorderedList) {
                    result.push('</ul>');
                    inUnorderedList = false;
                  }
                  if (inOrderedList) {
                    result.push('</ol>');
                    inOrderedList = false;
                  }
                  
                  if (trimmedLine && !trimmedLine.match(/^<h[3-5]/)) {
                    result.push(line + '<br>');
                  } else {
                    result.push(line);
                  }
                }
              }
              
              // 마지막 목록 닫기
              if (inUnorderedList) {
                result.push('</ul>');
              }
              if (inOrderedList) {
                result.push('</ol>');
              }
              
              html = result.join('\\n');
              console.log('Converted HTML:', html);
              return html;
            }
            
            // 실시간 미리보기
            function setupEventListeners() {
              console.log('Setting up event listeners');
              
              var textarea = document.getElementById('additional-review');
              var preview = document.getElementById('converted-preview');
              var previewContent = document.getElementById('preview-content');
              
              if (textarea && preview && previewContent) {
                textarea.addEventListener('input', function() {
                  console.log('Textarea input detected:', this.value);
                  var text = this.value;
                  
                  if (text.trim()) {
                    var convertedHTML = convertMarkdownToHTML(text);
                    previewContent.innerHTML = convertedHTML;
                    preview.style.display = 'block';
                  } else {
                    preview.style.display = 'none';
                  }
                });
                console.log('Event listeners set up successfully');
              } else {
                console.error('Could not find required elements');
              }
            }
            
            // 보고서에 추가하는 함수
            function convertAndAppendReview() {
              console.log('convertAndAppendReview called');
              
              var textarea = document.getElementById('additional-review');
              var reportContent = document.getElementById('report-content');
              
              console.log('textarea:', textarea);
              console.log('reportContent:', reportContent);
              
              if (!textarea || !reportContent) {
                console.error('Required elements not found');
                alert('오류: 필요한 요소를 찾을 수 없습니다.');
                return;
              }
              
              var text = textarea.value.trim();
              console.log('Input text:', text);
              
              if (!text) {
                alert('추가할 내용을 입력해주세요.');
                return;
              }
              
              try {
                var convertedHTML = convertMarkdownToHTML(text);
                console.log('Converted HTML for append:', convertedHTML);
                
                // 새로운 섹션 추가
                var newSection = document.createElement('div');
                newSection.style.marginTop = '30px';
                newSection.style.borderTop = '2px solid #007bff';
                newSection.style.paddingTop = '20px';
                
                var sectionTitle = document.createElement('h2');
                sectionTitle.style.color = '#333';
                sectionTitle.style.fontSize = '20px';
                sectionTitle.style.margin = '20px 0 10px 0';
                sectionTitle.style.borderBottom = '2px solid #333';
                sectionTitle.style.paddingBottom = '5px';
                sectionTitle.textContent = '보고서에 대한 평가';
                
                var sectionContent = document.createElement('div');
                sectionContent.innerHTML = convertedHTML;
                
                newSection.appendChild(sectionTitle);
                newSection.appendChild(sectionContent);
                reportContent.appendChild(newSection);
                
                console.log('Section added to report');
                
                // 입력창 초기화
                textarea.value = '';
                var preview = document.getElementById('converted-preview');
                if (preview) {
                  preview.style.display = 'none';
                }
                
                // 스크롤을 새로 추가된 내용으로 이동
                newSection.scrollIntoView({ behavior: 'smooth' });
                
                alert('검토 의견이 보고서에 추가되었습니다.');
                
              } catch (error) {
                console.error('Error in convertAndAppendReview:', error);
                alert('오류가 발생했습니다: ' + error.message);
              }
            }
            
            // 페이지 로드 시 초기화
            if (document.readyState === 'loading') {
              document.addEventListener('DOMContentLoaded', setupEventListeners);
            } else {
              setupEventListeners();
            }
            
            // Ctrl+P 단축키 지원
            document.addEventListener('keydown', function(e) {
              if (e.ctrlKey && e.key === 'p') {
                e.preventDefault();
                window.print();
              }
            });
            
            console.log('보고서 PDF 뷰가 로드되었습니다.');
          </script>
        </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();
    
    // 포커스를 새창에 맞춤
    newWindow.focus();
    
  } catch (error) {
    console.error('새창에서 보고서 열기 실패:', error);
    alert('보고서를 새창에서 여는데 실패했습니다.');
  }
};

/**
 * 간단한 텍스트 내용을 새창에서 프린트
 * @param title 문서 제목
 * @param content HTML 내용
 */
export const openTextInNewWindow = (title: string, content: string) => {
  try {
    const newWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
    
    if (!newWindow) {
      alert('팝업이 차단되었습니다. 팝업 차단을 해제하고 다시 시도해주세요.');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: "Malgun Gothic", Arial, sans-serif;
              line-height: 1.6;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            
            @media print {
              @page {
                size: A4;
                margin: 2cm;
              }
            }
          </style>
        </head>
        <body>
          <div style="text-align: right; margin-bottom: 20px;">
            <button onclick="window.print()" style="padding: 10px 20px; margin-right: 10px;">인쇄</button>
            <button onclick="window.close()" style="padding: 10px 20px;">닫기</button>
          </div>
          
          <h1>${title}</h1>
          <div>${content}</div>
        </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();
    newWindow.focus();
    
  } catch (error) {
    console.error('새창에서 텍스트 열기 실패:', error);
    alert('내용을 새창에서 여는데 실패했습니다.');
  }
}; 